import { useCallback, useRef, useState } from 'react';
import api from '../wrapper/API';
import useFlatRates from '../../hooks/useFlatRates';
import useAddOns from '../../hooks/useAddOns';
import useFlatRateRecipients from '../../hooks/useFlatRateRecipients';
import { MAIL_SLOT_LABEL } from '../../util/mailSlots';
import { NON_TEMPLATE_MAIL_SLOTS } from './templateModel';

// Fallback when GET /Textblock/Slots fails: labels from util/mailSlots; the two explicit slots
// (sent only by an explicit action) mirror AveOrga.Domain.Entities.MailSlots.
const FALLBACK_EXPLICIT_SLOTS = new Set(['payment_confirmed', 'announcement']);
const fallbackSlots = () => Object.keys(MAIL_SLOT_LABEL)
  .filter(slot => !NON_TEMPLATE_MAIL_SLOTS.has(slot))
  .map(slot => ({
    slot,
    label: MAIL_SLOT_LABEL[slot],
    isExplicit: FALLBACK_EXPLICIT_SLOTS.has(slot),
  }));

/**
 * Reference data of the template editor, loaded lazily and at most once per page visit (the page owns
 * the hook, so reopening the editor reuses it):
 * - textblocks: [{ id, name, label }] sorted by label (step targets + mail rules) — null until loaded
 * - slots: mail slot catalog (GET /Textblock/Slots) without NON_TEMPLATE_MAIL_SLOTS — null until loaded
 * - groupMailRules: { [groupId]: { [slot]: { enabled, textblockId } } } — the group's own rules, only
 *   used to explain what "Erben" resolves to in the mail tab (textblockId null = no mail).
 * - flatRateCatalog / flatRateRecipients: useFlatRates() / useFlatRateRecipients() for the tab
 *   "Pauschalen & Abzüge" (catalog + current role holders), loaded by its ensureLoaded() when the tab opens.
 * - addOnCatalog: useAddOns() for the tab "Zusätze" (shared add-on catalog), loaded by its ensureLoaded() when
 *   the tab opens.
 * A failed load is retried by the next ensure… call.
 */
export default function useTemplateReferenceData() {
  const [textblocks, setTextblocks] = useState(null);
  const [textblocksFailed, setTextblocksFailed] = useState(false);
  const [slots, setSlots] = useState(null);
  const [groupMailRules, setGroupMailRules] = useState({});
  const requested = useRef({ textblocks: false, slots: false, groups: new Set() });
  const flatRateCatalog = useFlatRates();
  const flatRateRecipients = useFlatRateRecipients();
  const addOnCatalog = useAddOns();

  const ensureTextblocks = useCallback(() => {
    if (requested.current.textblocks) return;
    requested.current.textblocks = true;
    setTextblocksFailed(false);
    api.get('/Textblock/GetAllNames?language=DE')
      .then(r => {
        // System blocks (2FA, newsletter confirmations, orga welcome) are never assignable to rules/steps.
        const list = (Array.isArray(r.data) ? r.data : [])
          .filter(tb => !tb.isSystemBlock)
          .map(tb => ({ id: Number(tb.id), name: tb.name ?? '', label: tb.frontendName || tb.name || `Baustein ${tb.id}` }))
          .sort((a, b) => a.label.localeCompare(b.label, 'de'));
        setTextblocks(list);
      })
      .catch(() => {
        requested.current.textblocks = false;
        setTextblocks([]);
        setTextblocksFailed(true);
      });
  }, []);

  const ensureSlots = useCallback(() => {
    if (requested.current.slots) return;
    requested.current.slots = true;
    api.get('/Textblock/Slots')
      .then(r => {
        // Only slots a template rule can change (the group welcome mail ignores templates).
        const list = (Array.isArray(r.data) ? r.data : [])
          .filter(s => !NON_TEMPLATE_MAIL_SLOTS.has(s.slot))
          .map(s => ({
            slot: s.slot,
            label: s.label || MAIL_SLOT_LABEL[s.slot] || s.slot,
            isExplicit: !!s.isExplicit,
          }));
        setSlots(list.length > 0 ? list : fallbackSlots());
      })
      .catch(() => {
        requested.current.slots = false;
        setSlots(fallbackSlots());
      });
  }, []);

  const ensureGroupMailRules = useCallback((groupIds) => {
    (groupIds ?? []).forEach(id => {
      const groupId = Number(id);
      if (requested.current.groups.has(groupId)) return;
      requested.current.groups.add(groupId);
      api.get(`/ParticipantGroup/${groupId}/MailRules`)
        .then(r => {
          const bySlot = {};
          (Array.isArray(r.data) ? r.data : []).forEach(rule => {
            bySlot[rule.slot] = {
              enabled: !!rule.enabled,
              textblockId: rule.textblockId ?? null,
            };
          });
          setGroupMailRules(prev => ({ ...prev, [groupId]: bySlot }));
        })
        .catch(() => {
          // Without the group rules the tab only says "Erbt die Regel der Gruppe".
          requested.current.groups.delete(groupId);
        });
    });
  }, []);

  return {
    textblocks,
    textblocksFailed,
    slots,
    groupMailRules,
    ensureTextblocks,
    ensureSlots,
    ensureGroupMailRules,
    flatRateCatalog,
    flatRateRecipients,
    addOnCatalog,
  };
}
